import { typographyTokens } from '../../../../theme/typographyTokens';
/**
 * SEPDetails Component
 * 
 * Admin page for viewing and managing SEP (System Enhancement Process) records.
 * Provides multi-select search functionality to fetch and display SEP details
 * with view and edit capabilities.
 * 
 * @module SEPDetails
 * @path src/frontend/features/Admin/Others/SEPDetails/SEPDetails.tsx
 */
import React, { useState, useMemo, useCallback } from 'react';
import { Paper, Typography, Box, TextField, Button, Autocomplete, useTheme } from '@mui/material';
import { useGetAllSEPIDsQuery, useGetMultipleSEPsQuery } from '../../../../../frontend/services/sepSlice';
import SEPDetailsTable from './SEPDetailsTable';

/**
 * Interface for SEP option in the Autocomplete dropdown
 * @property {number} id - Unique SEP identifier
 * @property {string} name - SEP display name
 */
export interface SepOption {
    id: number;
    name: string;
}

/**
 * SEPDetails - Main admin component for SEP workflow validation
 * 
 * Features:
 * - Multi-select Autocomplete for SEP selection
 * - Fetches all SEP IDs on mount for dropdown options
 * - Fetches detailed SEP data only when user clicks "GET DETAILS"
 * - Passes selected SEPs to SEPDetailsTable for display
 * 
 * @returns {JSX.Element} The SEP Details admin page
 */
const SEPDetails: React.FC = () => {
    // Track if user has initiated a search
    const [searched, setSearched] = useState(false);
    
    // Currently selected SEPs from the Autocomplete
    const [selectedSeps, setSelectedSeps] = useState<SepOption[]>([]);
    
    // Fetch all SEP IDs for the dropdown (lightweight query)
    const { data: sepIDs, error, isLoading } = useGetAllSEPIDsQuery();
    
    // Fetch full SEP details only for selected SEPs (skip if none selected)
    const { data: selectedSEPDetails } = useGetMultipleSEPsQuery(
        { ids: selectedSeps.map(sep => sep.id) },
        { skip: selectedSeps.length === 0 }
    );
    
    const theme = useTheme();

    /**
     * Memoized SEP options for Autocomplete dropdown
     * Transforms API response to SepOption format
     * Prevents unnecessary re-computation on every render
     */
    const sepOptions: SepOption[] = useMemo(() => {
        return Array.isArray(sepIDs)
            ? sepIDs.map((sep: any) => ({ id: sep.id, name: sep.name }))
            : [];
    }, [sepIDs]);

    /**
     * Handle GET DETAILS button click
     * Sets searched state to true only if SEPs are selected
     */
    const handleGetDetails = useCallback(() => {
        setSearched(false);
        if (selectedSeps.length > 0) {
            setSearched(true);
        }
    }, [selectedSeps.length]);

    /**
     * Handle CLEAR button click
     * Resets selection and hides table
     */
    const handleClear = useCallback(() => {
        setSelectedSeps([]);
        setSearched(false);
    }, []);

    /**
     * Handle Autocomplete selection change
     */
    const handleSelectionChange = useCallback((_event: React.SyntheticEvent, value: SepOption[]) => {
        setSelectedSeps(value);
    }, []);

    return (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 2 }}>
                <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl, mb: 2 }}>
                    Select SEPs
                </Typography>
                {isLoading ? (
                    <Typography sx={{ fontSize: typographyTokens.fontSizes.base, color: theme.palette.primary.main, mt: 2 }}>
                        Loading SEP list...
                    </Typography>
                ) : error ? (
                    <Typography sx={{ fontSize: typographyTokens.fontSizes.base, color: theme.palette.deepRed.main, mt: 2 }}>
                        Failed to load SEP list. Please try again later.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Autocomplete
                                multiple
                                options={sepOptions}
                                getOptionLabel={(option) => `${option.id} - ${option.name}`}
                                value={selectedSeps}
                                sx={{ minWidth: '100%', flexGrow: 1 }}
                                onChange={handleSelectionChange}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select SEP(s)"
                                        variant="outlined"
                                        margin="normal"
                                        sx={{ fontSize: typographyTokens.fontSizes.body, width: '100%' }}
                                    />
                                )}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    margin: '16px 0px 10px 0px',
                                    backgroundColor: theme.palette.primary.main,
                                    textTransform: 'none',
                                }}
                                onClick={handleGetDetails}
                            >
                                GET DETAILS
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{ margin: '16px 0px 10px 0px', textTransform: 'none' }}
                                onClick={handleClear}
                            >
                                CLEAR
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
            {/* Render table only when user has searched and has selections */}
            {searched && selectedSeps.length > 0 && (
                <SEPDetailsTable selectedSeps={selectedSEPDetails} theme={theme} />
            )}
        </Box>
    );
};

export default SEPDetails;

